/**
 * A Boo's mandate and its actions (lane BOO2, the platform's v2.3 cut): the creator's standing, bounded policy for the
 * Boo, issued once as the platform's `platform-mandate/1` (`POST /v2/agents/{id}/mandates`), paper by default; every
 * order and payout the Boo asks is a task action under it (`POST /v2/agents/{id}/actions`) and its receipt is shown as
 * the platform wrote it. algo.boo signs nothing: a live clause carries the owner's signed `owner-decision/1`, which the
 * owner supplies and the platform's issuer verifies.
 */
import { z } from 'zod'
import { WeiString } from '../primitives.js'

const Eth = z.string().regex(/^(0|[1-9]\d*)(\.\d{1,18})?$/)
/** Micro-USD, a positive integer string (1 USD = 1 000 000). */
const MicroUsd = z.string().regex(/^[1-9][0-9]{0,18}$/)

/** The owner's signed decision over a live mandate's terms, as the owner's key made it. */
export const OwnerDecisionInput = z
  .object({
    schema: z.literal('owner-decision/1'),
    decisionId: z.string().regex(/^[A-Za-z0-9_.:-]{1,64}$/),
    act: z.literal('mandate.live-clause'),
    subject: z.string().regex(/^[0-9a-f]{64}$/),
    decidedAt: z.number().int().min(1),
    keyId: z.string().regex(/^[A-Za-z0-9_.:-]{1,64}$/),
    signature: z.string().regex(/^[0-9a-f]{128}$/),
  })
  .strict()

/** `POST /agents/:handle/mandate`: the creator's standing policy for the Boo. */
export const IssueAgentMandate = z
  .object({
    /** The most one chat buy spends (ETH), the order clause's `maxUnits`. */
    buyAmountEth: Eth,
    /** The most the Boo's orders spend a day (ETH), the order clause's `maxPerPeriod`. */
    dailyBudgetEth: Eth,
    slippageBps: z.number().int().min(1).max(1000),
    /** Whether the Boo may pay out (to listed addresses, or an airdrop to its token's holders). */
    payouts: z.boolean(),
    /** The model credits the Boo's tasks may spend a day and over the mandate's life (micro-USD). */
    creditsPerDayMicroUsd: MicroUsd,
    spendCeilingMicroUsd: MicroUsd,
    /** Days until it expires (at most a year and a day). */
    days: z.number().int().min(1).max(366),
    /** `paper` (the default) or `live`; live needs the owner's signed decision over the terms. */
    mode: z.enum(['paper', 'live']).default('paper'),
    decision: OwnerDecisionInput.optional(),
  })
  .strict()
  .refine((v) => Number(v.buyAmountEth) > 0 && Number(v.dailyBudgetEth) >= Number(v.buyAmountEth), 'the daily budget covers at least one buy')
export type IssueAgentMandate = z.input<typeof IssueAgentMandate>

export const MandateClauseView = z.object({
  operation: z.string(),
  mode: z.enum(['paper', 'live']),
  maxUnits: z.string().nullable(),
  maxPerPeriod: z.string().nullable(),
  period: z.string().nullable(),
  maxRecipients: z.number().int().nullable(),
})

/** `GET /agents/:handle/mandate`: the Boo's platform agent and its standing mandate as the platform keeps them. */
export const AgentMandate = z.object({
  principal: z
    .object({ agentRef: z.string(), state: z.enum(['made', 'active', 'paused', 'retired']), refusal: z.string().nullable(), checkedAt: z.string().nullable() })
    .nullable(),
  mandate: z
    .object({
      mandateRef: z.string(),
      state: z.enum(['issued', 'revoked', 'expired']),
      name: z.string(),
      clauses: z.array(MandateClauseView),
      budget: z.object({ period: z.string(), creditsPerPeriod: z.string(), spendCeiling: z.string() }),
      issuedAt: z.string(),
      expiresAt: z.string(),
      slippageBps: z.number().int().nullable(),
    })
    .nullable(),
  /** Why nothing can be issued now, in plain words (no platform agent yet, the platform not connected). */
  unavailable: z.string().nullable(),
})
export type AgentMandate = z.infer<typeof AgentMandate>

/** One check the platform made before it performed an action. */
export const ActionCheckView = z.object({ check: z.string(), verdict: z.string(), source: z.string() })

/** An order or a payout the Boo asked, with the platform's receipt (null for a payout batch asked before the cut). */
export const AgentActionView = z.object({
  id: z.string().uuid(),
  operation: z.enum(['order', 'payout', 'airdrop']),
  origin: z.enum(['payout', 'chat']),
  batch: z.number().int(),
  state: z.enum(['preparing', 'requested', 'created', 'settled', 'refused']),
  receiptRef: z.string().nullable(),
  mandateRef: z.string().nullable(),
  taskRunRef: z.string().nullable(),
  refusal: z.object({ code: z.string(), words: z.string() }).nullable(),
  checks: z.array(ActionCheckView),
  outcome: z.record(z.string(), z.unknown()).nullable(),
  cost: z.record(z.string(), z.unknown()).nullable(),
  payoutRef: z.string().nullable(),
  amountWei: WeiString.nullable(),
  times: z.object({ occurredAt: z.string().nullable(), receivedAt: z.string().nullable(), availableAt: z.string().nullable(), settledAt: z.string().nullable() }),
  createdAt: z.string(),
})
export type AgentActionView = z.infer<typeof AgentActionView>

export const AgentActions = z.object({ actions: z.array(AgentActionView) })
